// ============================================================
// CreateTeam — forma za kreiranje novog tima.
// Logirani user može napraviti vlastiti tim koji se pojavi u
// javnom direktoriju (ako je visibility = public).
//
// Team dokument sadrži: name, slug, visibility, ownerUid, createdAt.
// Slug mora biti unikatan — provjera + rezervacija ide kroz
// transaction preko slugs/{slug} registry dokumenta.
// ============================================================

import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import bcrypt from 'bcryptjs';
import {
  db,
  collection,
  doc,
  runTransaction,
  serverTimestamp,
} from '../firebase';
import { ensureOwnerMembership } from '../utils/membership';
import '../App.css';

/* eslint-disable jsx-a11y/accessible-emoji */

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // ukloni akcente
    .replace(/đ/g, 'd')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);
};

const CreateTeam = ({ user }) => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (!slugManuallyEdited) {
      setSlug(slugify(val));
    }
  };

  const handleSlugChange = (e) => {
    setSlug(slugify(e.target.value));
    setSlugManuallyEdited(true);
  };

  const validate = () => {
    if (!user) return 'Morate biti prijavljeni za kreiranje tima.';
    if (!name.trim()) return 'Naziv tima je obavezan.';
    if (name.trim().length < 2) return 'Naziv tima mora imati bar 2 znaka.';
    if (name.trim().length > 40) return 'Naziv tima može imati najviše 40 znakova.';
    if (!slug) return 'Slug je obavezan.';
    if (slug.length < 2) return 'Slug mora imati bar 2 znaka.';
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug))
      return 'Slug smije sadržavati samo mala slova, brojke i crtice.';
    if (visibility === 'private') {
      if (!password) return 'Lozinka je obavezna za privatni tim.';
      if (password.length < 6) return 'Lozinka mora imati najmanje 6 znakova.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      // Pre-generiraj teamId da ga možemo upisati u oba dokumenta
      // (teams/{id} i slugs/{slug}) u istoj transakciji.
      const newTeamRef = doc(collection(db, 'teams'));
      const slugRef = doc(db, 'slugs', slug);

      const isPrivate = visibility === 'private';
      const passwordHash = isPrivate ? await bcrypt.hash(password, 10) : null;

      await runTransaction(db, async (tx) => {
        // Rezerviraj slug — fail-fast ako već postoji.
        const slugSnap = await tx.get(slugRef);
        if (slugSnap.exists()) {
          throw new Error(`SLUG_TAKEN:${slug}`);
        }

        tx.set(newTeamRef, {
          name: name.trim(),
          slug,
          visibility,
          ownerUid: user.uid,
          ownerEmail: user.email,
          description: '',
          logo: null,
          passwordEnabled: isPrivate,
          passwordHash,
          createdAt: serverTimestamp(),
        });

        tx.set(slugRef, {
          teamId: newTeamRef.id,
          ownerUid: user.uid,
          teamName: name.trim(),
          visibility,
          passwordEnabled: isPrivate,
          createdAt: serverTimestamp(),
        });
      });

      // Every owner is also a claimed team member so they appear in
      // roster/profile flows immediately after team creation.
      await ensureOwnerMembership(newTeamRef.id, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || name.trim(),
        createdBy: user.uid,
      });

      history.push('/directory');
    } catch (err) {
      console.error('❌ Greška pri kreiranju tima:', err);
      if (err.message && err.message.startsWith('SLUG_TAKEN:')) {
        setError(`Slug "${slug}" je već zauzet. Odaberi drugi.`);
      } else {
        setError(err.message || 'Greška pri kreiranju tima.');
      }
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="empty-state" style={{ margin: 40 }}>
        ⚠ Morate biti prijavljeni za kreiranje tima.
      </div>
    );
  }

  return (
    <div className="create-team-page">
      <section className="create-team-hero">
        <h1>Kreiraj svoj tim</h1>
        <p>
          Postavi ime, URL i vidljivost tima. Nakon kreiranja ti postaješ
          vlasnik i možeš dodavati igrače.
        </p>
      </section>

      <form onSubmit={handleSubmit} className="create-team-form">
        <div className="form-field">
          <label htmlFor="team-name">Naziv tima</label>
          <input
            id="team-name"
            type="text"
            value={name}
            onChange={handleNameChange}
            placeholder="npr. Zagreb Zmajevi"
            maxLength={40}
            autoFocus
          />
        </div>

        <div className="form-field">
          <label htmlFor="team-slug">
            URL tima <span className="form-hint">(slug)</span>
          </label>
          <div className="slug-input-wrapper">
            <span className="slug-prefix">/t/</span>
            <input
              id="team-slug"
              type="text"
              value={slug}
              onChange={handleSlugChange}
              placeholder="zagreb-zmajevi"
              maxLength={30}
            />
          </div>
          <span className="form-hint-below">
            Samo mala slova, brojke i crtice. Ovo će biti u adresi tima.
          </span>
        </div>

        <div className="form-field">
          <label>Vidljivost</label>
          <div className="visibility-toggle">
            <label
              className={`visibility-option ${
                visibility === 'public' ? 'selected' : ''
              }`}
            >
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={() => setVisibility('public')}
              />
              <span className="visibility-title">
                <span role="img" aria-label="globus">🌍</span> Javno
              </span>
              <span className="visibility-desc">
                Tim se pojavljuje u direktoriju i svatko može vidjeti statistiku.
              </span>
            </label>
            <label
              className={`visibility-option ${
                visibility === 'private' ? 'selected' : ''
              }`}
            >
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === 'private'}
                onChange={() => setVisibility('private')}
              />
              <span className="visibility-title">
                <span role="img" aria-label="lokot">🔒</span> Privatno
              </span>
              <span className="visibility-desc">
                Tim je skriven iz direktorija i zaštićen lozinkom.
              </span>
            </label>
          </div>
        </div>

        {visibility === 'private' && (
          <div className="form-field">
            <label htmlFor="team-password">Lozinka tima</label>
            <input
              id="team-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 6 znakova"
              autoComplete="new-password"
            />
          </div>
        )}

        {error && <div className="form-error">⚠ {error}</div>}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => history.push('/directory')}
            disabled={submitting}
          >
            Odustani
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Kreiranje…' : 'Kreiraj tim'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTeam;
